import { useState } from 'react'
import { UserRound } from 'lucide-react'
import { auth, users } from '@/api/endpoints'
import type { Role } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Err } from '@/components/Err'
import { Facts } from '@/components/Facts'
import { PageHead, Section } from '@/components/layout/Page'
import { PROVIDER_LABEL, roleLabel } from '@/domain/words'
import { useRefreshSession, useSession } from '@/hooks/useSession'
import { CHIP } from '@/views/vm/chips'
import { DASH } from '@/ui/format'
import { PasswordForm } from './PasswordForm'

// 내 정보(F-37, 모양은 F-47). 자기 계정을 보는 화면이라 관리자와 사용자가 같다.
// 읽을 것이 한 목록이고, 고칠 수 있는 줄에만 칩이 붙어 가벼운 대화 상자를 연다 —
// 아이디·이메일은 본인이 바꾸는 것이 아니라 칩이 없다.

type Open = 'name' | 'password' | 'role' | null

export default function MyPage() {
  const me = useSession()
  const [open, setOpen] = useState<Open>(null)
  const close = () => setOpen(null)
  const logins = [me.has_password === false ? null : '비밀번호', ...(me.identities ?? []).map((i) => PROVIDER_LABEL[i])].filter(Boolean)
  const chip = (what: Open, word: string) => (
    <button type="button" className={CHIP} onClick={() => setOpen(what)}>
      {word}
    </button>
  )
  const value = (v: string, action: React.ReactNode) => (
    <span className="flex items-center gap-2">
      {v}
      {action}
    </span>
  )
  return (
    <>
      <PageHead icon={<UserRound className="size-5" />} title="내 정보" facts={me.username} />

      <Section title="계정">
        <div className="rounded-lg border bg-card px-4 py-3">
          <Facts
            rows={[
              ['아이디', me.username],
              ['이름', value(me.name || DASH, chip('name', '수정'))],
              ['역할', value(roleLabel[me.role], BELOW[me.role] ? chip('role', '내려놓기') : null)],
              ['이메일', me.email ? `${me.email}${me.email_verified ? ' (인증됨)' : ' (미인증)'}` : DASH],
              ['로그인', value(logins.join(' · '), chip('password', '비밀번호 바꾸기'))],
            ]}
          />
        </div>
      </Section>


      <Dialog open={open !== null} onOpenChange={(v) => (v ? undefined : close())}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
          {open === 'name' ? <NameForm onClose={close} /> : null}
          {open === 'password' ? <PasswordForm onClose={close} /> : null}
          {open === 'role' ? <StepDown onClose={close} /> : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

function NameForm({ onClose }: { onClose: () => void }) {
  const me = useSession()
  const refresh = useRefreshSession()
  const [name, setName] = useState(me.name ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const trimmed = name.trim()
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!trimmed) return
    setBusy(true)
    setError(null)
    try {
      await auth.updateMe(trimmed)
      refresh()
      onClose()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }
  return (
    <form onSubmit={submit} className="grid gap-3">
      <DialogHeader>
        <DialogTitle>이름 수정</DialogTitle>
      </DialogHeader>
      <div className="grid gap-1.5">
        <Label htmlFor="me-name">이름</Label>
        <Input id="me-name" maxLength={80} value={name} disabled={busy} onChange={(e) => setName(e.target.value)} />
      </div>
      <Err error={error} />
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
          취소
        </Button>
        <Button type="submit" disabled={busy || !trimmed || trimmed === (me.name ?? '')}>
          {busy ? '저장 중' : '저장'}
        </Button>
      </DialogFooter>
    </form>
  )
}

// 권한 내려놓기(#311): 승격은 되돌아갈 수 있어야 한다. 다른 최고 관리자도 내릴 수 있지만(F-79)
// 자기 손으로 내려놓는 길도 있고, 한 단계 내려간다. 마지막 최고 관리자는 서버가 막는다.
// 역할 변경은 최고 관리자 세션만 받으므로 관리자에게는 내려놓을 단계가 없다 — 관리자를
// 사용자로 되돌리는 것은 최고 관리자의 일이다.
const BELOW: Record<string, { role: Role; after: string }> = {
  superadmin: { role: 'admin', after: '관리자는 모든 사용자와 자기가 관리하는 팀의 VM 을 봅니다.' },
}

function StepDown({ onClose }: { onClose: () => void }) {
  const me = useSession()
  const refresh = useRefreshSession()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const next = BELOW[me.role]
  if (!next) return null
  const go = async () => {
    setBusy(true)
    setError(null)
    try {
      await users.setRole(me.id, next.role)
      refresh()
      onClose()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }
  return (
    <div className="grid gap-3">
      <DialogHeader>
        <DialogTitle>권한 내려놓기</DialogTitle>
      </DialogHeader>
      <p className="text-[13px]">{next.after}</p>
      <p className="text-[13px]">되돌리려면 최고 관리자가 다시 올려 주어야 합니다.</p>
      <Err error={error} />
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
          취소
        </Button>
        <Button type="button" disabled={busy} onClick={go}>
          {busy ? '내려놓는 중' : '내려놓기'}
        </Button>
      </DialogFooter>
    </div>
  )
}
